/**
 * The way out of a `409` raised by a row that is deleted rather than live.
 *
 * Both admin listings sit on a unique index that ignores `deletedAt`, so a deleted row keeps its
 * key or slug reserved while the listing hides it. Showing the raw conflict here would tell someone
 * their identifier is taken by something they cannot see, cannot recreate and cannot reach — the
 * dead end this whole feature exists to close. So the screen names the row, says when it was
 * deleted, and offers the one action that unblocks them.
 *
 * The alternative — telling them to pick another key — is worse than it looks: it leaves the old
 * row holding the good name forever, and the next person hits the same wall.
 *
 * @module Shared/components
 */

import { css } from '@emotion/css';
import { UTModal } from '@group-link-one/gl-ui/utilities';
import type { JSX, ReactNode } from 'react';

const contentStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  padding-bottom: var(--space-s);
  font-size: var(--font-size-sm);
  text-wrap: pretty;
`;

const noticeStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-m);
  border: 1px solid var(--color-border-strong);
  border-left: 3px solid var(--color-border-strong);
  border-radius: var(--radius-card);
  background: var(--color-surface-hover);
`;

const lineStyle = css`
  margin: 0;
`;

const identifierStyle = css`
  font-family: var(--font-mono);
  font-weight: var(--font-weight-semibold);
`;

const hintStyle = css`
  margin: 0;
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

/** `2026-09-22T15:02:51.598Z` → `22/09/2026 15:02`. Absent or invalid dates print nothing. */
const formatDeletedAt = (value: string | null): string => {
  if (!value) return '';
  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
};

export interface RestoreHolderDialogProps {
  /** What the field is called here — "chave" or "slug". */
  identifierNoun: string;
  /** The value that came back rejected. */
  identifier: string;
  /** What the deleted row is — "funcionalidade" or "pesquisa". */
  entityNoun: string;
  deletedAt: string | null;
  isRestoring: boolean;
  /** Said after the restore offer, when restoring has a caveat of its own. */
  note?: ReactNode;
  onRestore: () => void;
  onClose: () => void;
}

export const RestoreHolderDialog = ({
  identifierNoun,
  identifier,
  entityNoun,
  deletedAt,
  isRestoring,
  note,
  onRestore,
  onClose,
}: RestoreHolderDialogProps): JSX.Element => {
  const when = formatDeletedAt(deletedAt);

  return (
    <UTModal
      open
      position='center'
      maxWidth='520px'
      hasButtonClose
      modalContentId='restore-holder-dialog'
      closeButtonId='restore-holder-dialog-close'
      title={`Esse ${identifierNoun} está com uma ${entityNoun} excluída`}
      content={
        <div className={contentStyle}>
          <div className={noticeStyle} role='status'>
            <p className={lineStyle}>
              <span className={identifierStyle}>{identifier}</span> pertence a uma {entityNoun} que
              foi excluída{when ? ` em ${when}` : ''}.
            </p>
            <p className={lineStyle}>
              Excluir é reversível e mantém o {identifierNoun} reservado, então ele não pode ser
              reaproveitado por uma {entityNoun} nova enquanto essa existir.
            </p>
          </div>

          <p className={lineStyle}>
            Restaurar aquela {entityNoun} é o caminho: ela volta para a lista, com o que já tinha, e
            você segue de lá. Escolher outro {identifierNoun} também funciona — mas deixa o nome bom
            preso numa {entityNoun} que ninguém vê.
          </p>

          {note ? <div className={hintStyle}>{note}</div> : null}
        </div>
      }
      customButtons={[
        {
          text: `Escolher outro ${identifierNoun}`,
          variant: 'secondary',
          disabled: isRestoring,
          onClick: onClose,
        },
        {
          text: `Restaurar a ${entityNoun}`,
          loading: isRestoring,
          disabled: isRestoring,
          onClick: onRestore,
        },
      ]}
      onClose={onClose}
    />
  );
};
